/**
 * PageHelmet — the ONE and ONLY <Helmet> in the entire public site.
 *
 * Reads from SEOContext. Only renders when page has set its own SEO data —
 * this prevents a default/fallback title from ever appearing alongside the
 * page-specific title (which caused the double-title bug).
 *
 * Keyed to pathname so React fully remounts it (replacing all tags) on
 * every route change.
 */

import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useSEOContext } from '@/hooks/use-seo';
import { 
  resolvePageKeyFromSlug, 
  resolveHubIdFromSlug, 
  resolveArticleIdFromSlug,
  getPagePath,
  buildUsefulHubPath,
  buildUsefulArticlePath,
  getUsefulRootPath,
  type Lang 
} from '@/lib/localizedRoutes';

const DEFAULT_OG_IMAGE = '/brand/5.png';
const SITE_NAME = 'SARGO';
const BASE_URL = 'https://sargogroup.com';

const SUPPORTED_LANGS = ['ru', 'en', 'uz'] as const;

/** Returns the lang prefix from pathname (e.g. '/ru/catalog' → 'ru'), or null */
function getLangPrefix(pathname: string): string | null {
  const seg = pathname.split('/')[1];
  return (SUPPORTED_LANGS as readonly string[]).includes(seg) ? seg : null;
}

/** Strip the lang prefix segment, returning the rest with a leading slash.
 *  '/ru/catalog/foo' → '/catalog/foo'
 *  '/ru' → '/'
 *  '/catalog' → '/catalog'  (no lang prefix — returned as-is)
 */
function stripLang(pathname: string): string {
  const parts = pathname.split('/');
  if ((SUPPORTED_LANGS as readonly string[]).includes(parts[1])) {
    const rest = parts.slice(2).join('/');
    return rest ? `/${rest}` : '/';
  }
  return pathname;
}

/** Non-public paths where we must NOT inject hreflang */
function isPublicPath(pathname: string): boolean {
  return !pathname.startsWith('/admin') && !pathname.startsWith('/crm');
}

export function PageHelmet() {
  const { data } = useSEOContext();
  const location = useLocation();

  // Do NOT render anything until the page component has provided its SEO data.
  if (!data) return null;

  const {
    title,
    description,
    canonical,
    ogType,
    ogImage,
    schema,
  } = data;

  const pathname = location.pathname;
  const isPublic = isPublicPath(pathname);
  const langPrefix = getLangPrefix(pathname); // e.g. 'ru', 'en', 'uz', or null
  const pagePath = stripLang(pathname);       // path without lang, e.g. '/catalog/foo'

  // ── Localized Path Resolver ────────────────────────────────────────────────
  // Reusable logic to get the correct localized URL for the current page
  // in ANY target language.
  const getLocalizedHref = (targetLang: Lang): string => {
    const currentLang = (langPrefix as Lang) || 'ru';
    const segments = pathname.split('/').filter(Boolean);
    
    // If we have a lang prefix, skip it to get path segments
    const pathSegments = langPrefix ? segments.slice(1) : segments;
    
    if (pathSegments.length === 0) return `${BASE_URL}/${targetLang}`;
    
    const firstSlug = pathSegments[0];
    const pageKey = resolvePageKeyFromSlug(firstSlug, currentLang);
    
    // 1. Useful section (articles, hubs, root)
    if (pageKey === 'useful') {
      if (pathSegments.length === 1) {
        return `${BASE_URL}${getUsefulRootPath(targetLang)}`;
      } else if (pathSegments.length === 2) {
        const hubSlug = pathSegments[1];
        const hubId = resolveHubIdFromSlug(hubSlug, currentLang) || hubSlug;
        return `${BASE_URL}${buildUsefulHubPath(targetLang, hubId)}`;
      } else if (pathSegments.length >= 3) {
        const hubSlug = pathSegments[1];
        const articleSlug = pathSegments[2];
        const hubId = resolveHubIdFromSlug(hubSlug, currentLang) || hubSlug;
        const articleId = resolveArticleIdFromSlug(hubSlug, articleSlug, currentLang) || articleSlug;
        return `${BASE_URL}${buildUsefulArticlePath(targetLang, hubId, articleId)}`;
      }
    }
    
    // 2. Standalone pages
    if (pageKey) {
      return `${BASE_URL}${getPagePath(pageKey, targetLang)}`;
    }
    
    // Fallback: just swap the lang prefix
    return `${BASE_URL}/${targetLang}/${pathSegments.join('/')}`;
  };

  // ── Canonical ──────────────────────────────────────────────────────────────
  // For useful articles (and other localized pages), we ALWAYS build from 
  // localized logic to override any hardcoded or incorrect 'canonical' props
  // coming from individual components.
  const finalCanonical: string = (() => {
    if (isPublic && langPrefix) {
      return getLocalizedHref(langPrefix as Lang);
    }

    if (canonical) {
      // If the canonical is already absolute, use as-is
      if (canonical.startsWith('http')) return canonical;
      // If there is a lang prefix in the current URL, keep it
      if (langPrefix) {
        // If the provided canonical already has a lang prefix, use it
        if (getLangPrefix(canonical)) return `${BASE_URL}${canonical}`;
        // Otherwise prepend the current lang prefix
        return `${BASE_URL}/${langPrefix}${canonical === '/' ? '' : canonical}`;
      }
      return `${BASE_URL}${canonical}`;
    }
    // No explicit canonical — build from current pathname
    return `${BASE_URL}${pathname}`;
  })();

  const finalImage = ogImage
    ? (ogImage.startsWith('http') ? ogImage : `${BASE_URL}${ogImage}`)
    : `${BASE_URL}${DEFAULT_OG_IMAGE}`;

  const type = ogType || 'website';

  // ── hreflang alternates ────────────────────────────────────────────────────
  // Only inject for public pages that are inside a language prefix.
  // x-default points to the /ru version.
  const hreflangs = (isPublic && langPrefix)
    ? SUPPORTED_LANGS.map(lang => ({
        lang,
        href: getLocalizedHref(lang as Lang)
      }))
    : [];

  const xDefaultHref = hreflangs.find(h => h.lang === 'ru')?.href || null;

  return (
    // key=pathname forces Helmet to fully replace all tags on every route change
    <Helmet key={pathname}>
      <title>{title}</title>

      <meta name="description" content={description} />
      <link rel="canonical" href={finalCanonical} />

      {/* hreflang alternate links — only for multilingual public pages */}
      {hreflangs.map(({ lang, href }) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={href} />
      ))}
      {xDefaultHref && (
        <link rel="alternate" hrefLang="x-default" href={xDefaultHref} />
      )}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:url" content={finalCanonical} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={finalImage} />

      {/* Schema.org — one <script> per schema object (nulls filtered out) */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
}
